import { Link } from 'react-router-dom';

export default function SoyProteinSelectionGuide() {
  return (
    <div className="min-h-screen bg-white text-[#5D4037]">
      {/* Header */}
      <header className="bg-white border-b border-[#E0D7D0] sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <span className="font-bold text-xl text-[#bc6355]">プロテイン選びの教科書</span>
        </div>
      </header>

      {/* Breadcrumb */}
      <nav className="bg-[#FAF9F8] border-b border-[#E0D7D0]">
        <div className="container mx-auto px-6 py-3">
          <ul className="flex items-center gap-2 text-sm">
            <li>
              <a
                href="https://soyproteinranking.com/"
                className="flex items-center gap-2 text-[#8D6E63] hover:text-[#bc6355] transition-colors whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center text-[#bc6355]">
                  <i className="ri-home-4-line"></i>
                </span>
                <span>トップ</span>
              </a>
            </li>
            <li className="text-[#BCAAA4]">/</li>
            <li className="text-[#5D4037] whitespace-nowrap">
              初めてのソイプロテイン選び
            </li>
          </ul>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        {/* Hero Section */}
        <section className="mb-12">
          <h1 className="text-3xl font-bold text-white mb-6 bg-gradient-to-r from-[#bc6355] to-[#d4847a] px-6 py-4 rounded-lg leading-tight">
            初めてのソイプロテイン選び！失敗しないチェックポイントと基準
          </h1>

          <p className="text-[#5D4037] leading-relaxed mb-4">
            「ソイプロテインを始めてみたいけれど、どれを買えば失敗しないの？」と迷っていませんか？
          </p>

          <p className="text-[#5D4037] leading-relaxed">
            最近は種類が豊富になりすぎて、パッケージのデザインだけで選ぶと「味が合わなくて飲み切れない」「目的に合っていなかった」という失敗も。
            <span className="text-[#bc6355] font-semibold underline decoration-[#F9EBEA] decoration-4">
              後悔しないための3つの基準
            </span>
            を知って、あなたにぴったりの1袋を見つけましょう。
          </p>
        </section>

        {/* Feature Section (3 Checkpoints) */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#5D4037] mb-6 pb-3 border-b-4 border-[#bc6355]">
            必ず確認すべき3つのチェックポイント
          </h2>

          <p className="text-[#5D4037] leading-relaxed mb-6">
            ソイプロテインを選ぶ際、プロが最初に見るのは「裏面の成分表」と「続けやすさ」のバランスです。
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="p-4 bg-[#FDF8F3] rounded-lg border border-[#E8DDD4]">
              <h4 className="font-bold text-[#bc6355] mb-2">1杯あたりの価格</h4>
              <p className="text-sm">
                1杯あたりの金額が100円以下のものを選ぶと続けやすいです。
              </p>
            </div>
            <div className="p-4 bg-[#FDF8F3] rounded-lg border border-[#E8DDD4]">
              <h4 className="font-bold text-[#bc6355] mb-2">添加物の少なさ</h4>
              <p className="text-sm">
                毎日飲むものだからこそ、甘味料や保存料ができるだけシンプルなものを選びましょう。
              </p>
            </div>
            <div className="p-4 bg-[#FDF8F3] rounded-lg border border-[#E8DDD4]">
              <h4 className="font-bold text-[#bc6355] mb-2">溶けやすさ・味</h4>
              <p className="text-sm">
                口コミで「ダマにならないか」をチェック。最初はココア味など定番が失敗しません。
              </p>
            </div>
          </div>
        </section>

        {/* Comparison Table (By Goal) */}
        <section className="mb-12">
          <h3 className="text-xl font-bold text-[#5D4037] mb-6">
            【目的別】選ぶべきソイプロテインの基準
          </h3>

          <div className="overflow-hidden rounded-lg border border-[#E0D7D0] shadow-sm mb-8 bg-white">
            <table className="w-full text-center">
              <thead>
                <tr className="bg-[#FAF9F8]">
                  <th className="px-6 py-4 text-sm font-semibold text-[#5D4037] border-b border-[#E0D7D0]">
                    あなたの目的
                  </th>
                  <th className="px-6 py-4 text-sm font-semibold text-[#bc6355] border-b border-[#E0D7D0]">
                    重視する項目
                  </th>
                  <th className="px-6 py-4 text-sm font-semibold text-[#5D4037] border-b border-[#E0D7D0]">
                    おすすめのタイプ
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E0D7D0]">
                <tr>
                  <td className="px-6 py-4 bg-[#FAF9F8] font-semibold text-sm">本格ダイエット</td>
                  <td className="px-6 py-4 text-[#5D4037] text-sm">低糖質・低脂質</td>
                  <td className="px-6 py-4 text-[#5D4037] text-sm">プレーン系・機能性表示食品</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 bg-[#FAF9F8] font-semibold text-sm">美容・美肌</td>
                  <td className="px-6 py-4 text-[#5D4037] text-sm">ビタミン・コラーゲン</td>
                  <td className="px-6 py-4 text-[#5D4037] text-sm">美容成分配合タイプ</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 bg-[#FAF9F8] font-semibold text-sm">健康維持</td>
                  <td className="px-6 py-4 text-[#bc6355] font-semibold text-sm">
                    おいしさ・続けやすさ
                  </td>
                  <td className="px-6 py-4 text-[#5D4037] text-sm">苦味が気にならないフレーバー</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Pros & Cons Section (Powder vs Ready-to-drink) */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#5D4037] mb-6 pb-3 border-b-4 border-[#bc6355]">
            コスパ重視？手軽さ重視？
          </h2>

          <div className="mb-8">
            <h4 className="text-lg font-bold text-[#5D4037] mb-4 border-l-4 border-[#bc6355] pl-3">
              大袋タイプ：長期的に続けるなら最強の選択
            </h4>
            <p className="text-[#5D4037] leading-relaxed">
              一番のメリットは<strong>「1食あたりの安さ」</strong>です。1kg単位で購入すれば、1食100円以下に抑えることも可能。自分の好きな飲み物（豆乳やアーモンドミルク）で割るなど、アレンジの幅も広がります。
            </p>
          </div>

          <div className="mb-8">
            <h4 className="text-lg font-bold text-[#5D4037] mb-4 border-l-4 border-[#BCAAA4] pl-3">
              個包装・ドリンクタイプ：外出時や味見に最適
            </h4>
            <p className="text-[#5D4037] leading-relaxed">
              <strong>「計量の手間がない」「持ち運びができる」</strong>点が最大の魅力です。初めて飲むブランドで味が不安なときは、まずはコンビニ等のドリンクタイプや個包装で試してみるのが賢い方法です。
            </p>
          </div>
        </section>

        {/* Recommended Steps Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#5D4037] mb-6 pb-3 border-b-4 border-[#bc6355]">
            失敗しないための購入ステップ
          </h2>

          <div className="bg-gradient-to-br from-[#FAF9F8] to-[#F3EFEF] rounded-xl p-8 border border-[#E0D7D0] shadow-sm">
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#bc6355] text-white flex items-center justify-center text-sm font-semibold mt-0.5">
                  1
                </div>
                <p className="text-[#5D4037]">
                  まずは自分の「目的」を決める（ダイエットか、美容か、栄養補給か）
                </p>
              </li>
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#bc6355] text-white flex items-center justify-center text-sm font-semibold mt-0.5">
                  2
                </div>
                <p className="text-[#5D4037]">
                  お試しパックや少容量（300g程度）から始めて、飲みやすさを確認する
                </p>
              </li>
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#bc6355] text-white flex items-center justify-center text-sm font-semibold mt-0.5">
                  3
                </div>
                <p className="text-[#5D4037]">
                  味が気に入ったら、コスパの良い大袋（1kg〜）に切り替えて習慣化する
                </p>
              </li>
            </ul>
          </div>
        </section>

        {/* Summary Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#5D4037] mb-6 pb-3 border-b-4 border-[#bc6355]">
            まとめ
          </h2>

          <div className="bg-[#bc6355] text-white rounded-xl p-8 shadow-sm">
            <div className="space-y-4 leading-relaxed">
              <p>
                ソイプロテイン選びで最も大切なのは、<strong>「自分が無理なく毎日続けられるかどうか」</strong>です。
              </p>
              <p>
                どんなに成分が良くても、味が苦手だったり価格が高すぎたりすると続きません。まずは「味」と「目的」のバランスが取れた1袋を見つけてみてください。
              </p>
              <p className="pt-4 text-center font-bold border-t border-[#d4847a]">
                納得のいくプロテイン選びで、新しい健康習慣をスタートさせましょう！
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}